// [목적] 공개 프로필(usePublicProfile) + 본인 User 상세(useMe) + 사용자 폴리오(useUserFolios) +
//        홈 통계(useUserStats) + 팔로워/팔로잉 목록(useFollowList) TanStack Query 훅
// [이유] FEED-01 — ProfilePage 에서 handle 기준 공개 프로필 조회 + 본인 판별(isMe) 분기에 필요.
//         useAuth().user 는 MeOutput(auth 스키마) 이고, useMe 는 UserMe(user 스키마 — emailMasked 포함)로 별도 캐시
//         M7 P1: useUserStats — GET /users/me/stats(홈 통계 카드).
//         M7 P2: useFollowList — GET /users/:handle/followers·following(팔로워/팔로잉 목록 페이지).
// [사이드 이팩트] queryKeys.user.profile(handle) 캐시 — ProfilePage 언마운트 후 gcTime(5분) 동안 보존.
//                 getPublicProfile 404 시 ApiError(404) throw → ErrorBoundary 가 캐치
//                 useFollowList 는 throwOnError=false — 페이지가 error 를 직접 throw 하여 403(비공개
//                 프로필) 을 ErrorBoundary 로 위임(CollectionPage.tsx 와 동일 관례).
// [수정 시 고려사항] usePublicProfile 의 enabled 는 handle 이 truthy 일 때만 — 빈 string 방어.
//                   useMe 는 authenticated 상태 전용 — guest 시 enabled=false 로 요청 차단
//                   useUserStats 도 authenticated 전용(JwtAuthGuard).

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { queryKeys } from '../api/queryKeys.js';
import {
  getPublicProfile,
  getMe,
  getUserFolios,
  getMyStats,
  getFollowers,
  getFollowing,
  type UserFoliosPage,
  type UserFollowPage,
} from '../api/user.client.js';
import { useAuth } from './useAuth.js';
import type { UserPublicProfile, UserMe, UserStats } from '@gangwoncanvas/types';

// ─── usePublicProfile ────────────────────────────────────────────────────────

/**
 * [목적] GET /users/:handle 공개 프로필 조회 훅
 * [이유] FEED-01 ProfilePage — /u/:handle 라우트에서 handle 로 프로필 렌더
 * [사이드 이팩트] 404 → ApiError throw → ErrorBoundary fallback. 비인증 요청 허용
 * [수정 시 고려사항] throwOnError=false — 페이지가 error 상태를 직접 읽고 ErrorBoundary 로 throw 여부를 결정.
 *                   테스트 환경에서 throwOnError=true 시 미처리 Promise rejection 으로 vitest 가 실패함.
 */
export function usePublicProfile(handle: string | undefined): {
  profile: UserPublicProfile | undefined;
  isLoading: boolean;
  error: unknown;
  isError: boolean;
  refetch: () => void;
} {
  const { data, isLoading, error, isError, refetch } = useQuery({
    queryKey: queryKeys.user.profile(handle ?? ''),
    queryFn: () => getPublicProfile(handle!),
    enabled: Boolean(handle),
    staleTime: 1000 * 60 * 2, // 2분
    throwOnError: false,
  });

  // [목적] G1-2: 페이지가 isError 시 무한 스켈레톤 대신 InlineErrorState(재시도)를 렌더할 수 있도록 노출
  // [이유] 기존엔 isLoading=false 이지만 data=undefined(=error) 인 경우를 페이지가 "아직 로딩 중"으로
  //        오인해 ProfileSkeleton 이 영구 표시되는 버그가 있었다(isLoading||!profile 분기).
  // [사이드 이팩트] 없음 — refetch 는 동일 queryKey 재요청(신규 네트워크 호출 1회).
  // [수정 시 고려사항] refetch 는 void 반환 래핑 — 호출부가 Promise 를 기다릴 필요 없는 용도(버튼 onClick)만 지원.
  return { profile: data, isLoading, error, isError, refetch: () => void refetch() };
}

// ─── useMe ───────────────────────────────────────────────────────────────────

/**
 * [목적] GET /users/me 본인 User 상세 조회 훅 (UserMe 스키마 — emailMasked 포함)
 * [이유] FEED-01 — Settings/ProfileEditPage 에서 민감 필드(emailMasked) 포함 본인 정보 필요.
 *         useAuth().user 의 MeOutput 과 스키마가 다르므로 별도 캐시
 * [사이드 이팩트] authenticated 상태 전용 — guest 시 enabled=false 로 요청 차단
 * [수정 시 고려사항] user.client.ts getMe() 는 GET /users/me (auth.client.ts me() 의 GET /auth/me 와 별개)
 */
export function useMe(): {
  me: UserMe | undefined;
  isLoading: boolean;
  error: unknown;
} {
  const { status } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.user.me(),
    queryFn: getMe,
    enabled: status === 'authenticated',
    staleTime: 1000 * 60 * 2, // 2분
    throwOnError: false, // Settings 에서 비표시 에러 처리
  });

  return { me: data, isLoading, error };
}

// ─── useUserFolios ────────────────────────────────────────────────────────────

/**
 * [목적] GET /users/:handle/folios 첫 페이지 조회 (ProfilePage 폴리오 탭 단순 목록)
 * [이유] FEED-01 ProfilePage 폴리오 탭 — 무한스크롤 없이 첫 페이지 20개 표시
 * [사이드 이팩트] queryKeys.user.folios(handle) 캐시
 * [수정 시 고려사항] 무한스크롤이 필요해지면 useInfiniteQuery 로 교체 (FEED-06 패턴 참고)
 */
export function useUserFolios(handle: string | undefined): {
  folios: UserFoliosPage | undefined;
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.user.folios(handle ?? ''),
    queryFn: () => getUserFolios(handle!),
    enabled: Boolean(handle),
    staleTime: 1000 * 60 * 2, // 2분
    throwOnError: false,
  });

  return { folios: data, isLoading, error };
}

// ─── useUserStats ─────────────────────────────────────────────────────────────

/**
 * [목적] GET /users/me/stats 홈 통계 카드(내 폴리오/받은 좋아요/Fork됨) 조회 훅
 * [이유] M7 P1 — HomePage 통계 카드 mock(0/221/34 하드코딩) 실데이터 연결
 * [사이드 이팩트] authenticated 상태 전용 — guest 시 enabled=false 로 401 요청 자체를 차단(호출부가 섹션 숨김 처리)
 * [수정 시 고려사항] user.client.ts getMyStats() 는 JwtAuthGuard 필수 — 비로그인 호출 금지
 */
export function useUserStats(): {
  stats: UserStats | undefined;
  isLoading: boolean;
  error: unknown;
} {
  const { status } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.user.stats(),
    queryFn: getMyStats,
    enabled: status === 'authenticated',
    staleTime: 1000 * 60 * 2,
    throwOnError: false,
  });

  return { stats: data, isLoading, error };
}

// ─── useFollowList ────────────────────────────────────────────────────────────

/**
 * [목적] GET /users/:handle/followers · /following cursor 무한스크롤 훅 (팔로워/팔로잉 공용)
 * [이유] M7 P2 — ProfilePage 팔로워/팔로잉 카운트 클릭 시 이동하는 신규 목록 페이지 데이터 소스.
 * [사이드 이팩트] throwOnError=false — 비공개 프로필(403 user.FORBIDDEN) 을 페이지가 직접 throw 하여
 *                 ErrorBoundary(CollectionPage.tsx 와 동일 패턴)로 위임할 수 있도록 error 를 그대로 노출.
 * [수정 시 고려사항] kind 에 따라 getFollowers/getFollowing + queryKeys.user.followers/following 분기.
 *                   handle 이 undefined 이면 enabled=false.
 */
export function useFollowList(
  handle: string | undefined,
  kind: 'followers' | 'following',
): {
  pages: UserFollowPage[];
  items: UserFollowPage['items'];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  error: unknown;
} {
  const fetcher = kind === 'followers' ? getFollowers : getFollowing;
  const queryKey =
    kind === 'followers'
      ? queryKeys.user.followers(handle ?? '')
      : queryKeys.user.following(handle ?? '');

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error } =
    useInfiniteQuery({
      queryKey,
      queryFn: ({ pageParam }) =>
        fetcher(handle!, { cursor: pageParam as string | undefined, limit: 20 }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      enabled: Boolean(handle),
      staleTime: 1000 * 60 * 2,
      throwOnError: false,
    });

  return {
    pages: data?.pages ?? [],
    items: data?.pages.flatMap((p) => p.items) ?? [],
    fetchNextPage: () => void fetchNextPage(),
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    isLoading,
    error,
  };
}
